/**
 * Utility functions to handle external links in PWA
 */

/**
 * Checks if a URL is external (different origin)
 */
export const isExternalUrl = (url: string): boolean => {
  if (!url) return false;
  
  // Check if it starts with http:// or https://
  if (url.startsWith('http://') || url.startsWith('https://')) {
    try {
      const urlObj = new URL(url);
      const currentOrigin = window.location.origin;
      return urlObj.origin !== currentOrigin;
    } catch (e) {
      // If URL parsing fails, assume it's external
      return true;
    }
  }
  
  return false;
};

/**
 * Checks if the app is running in standalone/PWA mode
 */
export const isPWAStandalone = (): boolean => {
  // Check for standalone display mode (iOS)
  if ((window.navigator as any).standalone === true) {
    return true;
  }
  
  // Check for standalone display mode (Android/Chrome)
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  
  // Check if running in fullscreen mode
  if (window.matchMedia('(display-mode: fullscreen)').matches) {
    return true;
  }
  
  // Check if running in minimal-ui mode
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return true;
  }
  
  return false;
};

/**
 * Opens an external URL in a new tab/window
 * In PWA mode, this ensures the link opens in the system browser
 */
export const openExternalUrl = (url: string): void => {
  if (!url) return;
  
  // For external URLs, always open in a new tab
  // This works both in browser and PWA mode
  window.open(url, '_blank', 'noopener,noreferrer');
};

/**
 * Handles link clicks - opens external URLs in new tab, internal URLs with router
 */
export const handleLinkClick = (
  url: string,
  e?: React.MouseEvent<HTMLAnchorElement>
): void => {
  if (e) {
    e.preventDefault();
    e.stopPropagation();
  }
  
  if (isExternalUrl(url)) {
    openExternalUrl(url);
  } else {
    // For internal URLs, you would use React Router navigation
    // This function is mainly for external URLs
    window.location.href = url;
  }
};

